import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { FOOD_PROFILE, HOUSEHOLD_AISLES, HOUSEHOLD_PROFILE, LEVELS, freezable, isFreezer, openedName, allergenIds, canBook, editsMasterData, PANTRY_PROFILES, aisleOf, baseProduct, levelQty, pantryName, pantrySize, profileById, quantityAt, searchBaseProducts, stockLines, suggestedCountBy, suggestedKeep, suggestedUnit, uuidv7 } from "@lagerlog/core";
import type { BaseProduct, Booking, Level, PantryTablePlan, Profile } from "@lagerlog/core";
import { euro, findArticles, pricesShown } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import { fmt } from "./Book.tsx";
import { FieldInput, History, editable, shown } from "./Wines.tsx";
import { Emoji } from "./Emoji.tsx";
import { Reserved } from "./Reserved.tsx";
import { useWide } from "../wide.ts";
import { unsaved } from "../unsaved.ts";
import { compareLagers } from "../brand.ts";
import { PantryTableCard, PantryTablePreview } from "./PantryTable.tsx";

type View = { kind: "list" } | { kind: "show"; id: string } | { kind: "edit"; id: string | null; profile: string } | { kind: "table"; plan: PantryTablePlan; file: string };
type Stock = Map<string, { qty: number; value: number; at: Map<string, number> }>;

/** Stock per article in the storages of this device, and per location. */
export function usePantryStock(): Stock {
  const { store, version } = useStore();
  const ledger = useLedger();
  return useMemo(() => {
    const out: Stock = new Map();
    if (!ledger) return out;
    const scope = new Set(store.config?.lagers ?? []);
    for (const line of stockLines(ledger)) {
      if (!scope.has(line.lager)) continue;
      const s = out.get(line.article) ?? { qty: 0, value: 0, at: new Map<string, number>() };
      s.qty += line.qty;
      s.value += line.value;
      s.at.set(line.location, (s.at.get(line.location) ?? 0) + line.qty);
      out.set(line.article, s);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, version]);
}

/** The quantity in the column on the right, one part per line („2 Dosen +“ / „1 angebrochen“). */
function Side({ a, qty }: { a: Article; qty: number }) {
  return (
    <span className={`side ${qty ? "" : "muted"}`}>
      {(qty ? fmt(a, qty) : "–").split(" + ").map((part, i, parts) => (
        <span key={i}>{i < parts.length - 1 ? `${part} +` : part}</span>
      ))}
    </span>
  );
}

/**
 * Vorräte (docs/18-vorratsbuch.md, V0-5 and V0-6): the food and household goods of the household,
 * search and filter by aisle, one with its stock, a tap for an opened pack, add or change one.
 * On a wide screen the list stays on the left and the chosen article shows on the right.
 */
export function Pantry() {
  const catalog = useCatalog();
  const ledger = useLedger();
  const wide = useWide();
  const stock = usePantryStock();
  // „#/vorraete?neu=Passata“ (Buchen found nothing, A4; Start of a new household, C2): the form for a new one, the word in the base product.
  const [seed] = useState(() => {
    const word = new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("neu");
    if (word !== null) history.replaceState(null, "", "#/vorraete");
    return word;
  });
  const [view, setView] = useState<View>(() => {
    if (seed === null) return { kind: "list" };
    return { kind: "edit", id: null, profile: profileForBase(searchBaseProducts(seed, 1, { drinks: true })[0]).id };
  });
  const [query, setQuery] = useState("");
  const [aisle, setAisle] = useState("");
  const [tableDone, setTableDone] = useState<string | null>(null);

  if (!catalog || !ledger) return null;
  if (view.kind === "table") {
    return (
      <PantryTablePreview
        plan={view.plan}
        file={view.file}
        catalog={catalog}
        onDone={(message) => {
          setTableDone(message);
          setView({ kind: "list" });
        }}
      />
    );
  }
  if (view.kind === "edit") {
    const article = view.id ? catalog.articles.get(view.id) : undefined;
    return <PantryForm article={article} profile={profileById(article?.fields.profile as string | undefined ?? view.profile)} wide={wide} seed={article ? "" : (seed ?? "")} onDone={(id) => setView(id ? { kind: "show", id } : { kind: "list" })} />;
  }
  const shownId = view.kind === "show" ? view.id : null;
  const detail = shownId ? catalog.articles.get(shownId) : undefined;
  const list = (
    <PantryList
      catalog={catalog}
      stock={stock}
      query={query}
      setQuery={setQuery}
      aisle={aisle}
      setAisle={setAisle}
      selected={wide ? shownId : null}
      onShow={(id) => setView({ kind: "show", id })}
      onNew={(profile) => setView({ kind: "edit", id: null, profile })}
      onTable={(plan, file) => {
        setTableDone(null);
        setView({ kind: "table", plan, file });
      }}
      tableDone={tableDone}
    />
  );
  if (!wide) {
    if (detail) return <PantryDetail a={detail} catalog={catalog} stock={stock} onBack={() => setView({ kind: "list" })} setView={setView} />;
    return list;
  }
  return (
    <div className="split">
      <div className="pane">{list}</div>
      <div className="pane side-pane">
        {detail ? (
          <PantryDetail a={detail} catalog={catalog} stock={stock} setView={setView} />
        ) : (
          <div className="card placeholder">
            <p>Links etwas wählen: Hier stehen dann Bestand je Lagerort, MHD und alle Angaben.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PantryList(props: {
  catalog: Catalog;
  stock: Stock;
  query: string;
  setQuery: (q: string) => void;
  aisle: string;
  setAisle: (a: string) => void;
  selected: string | null;
  onShow: (id: string) => void;
  onNew: (profile: string) => void;
  onTable: (plan: PantryTablePlan, file: string) => void;
  tableDone: string | null;
}) {
  const { catalog, stock, query, aisle } = props;
  const { store } = useStore();
  // Master data only for owners and managers (F20).
  const edits = editsMasterData(store.rights().role);
  const all = [...catalog.articles.values()];
  const aisles = [...new Set(all.map((a) => String(a.fields.aisle ?? "")).filter(Boolean))];
  const order = (name: string) => PANTRY_ORDER.indexOf(name);
  aisles.sort((x, y) => order(x) - order(y));
  const found = query.trim() ? findArticles(all, query) : all.sort((x, y) => order(String(x.fields.aisle)) - order(String(y.fields.aisle)) || x.name.localeCompare(y.name, "de"));
  const rows = found.filter((a) => !aisle || a.fields.aisle === aisle);
  return (
    <div className="wines pantry">
      <div className="row">
        <input placeholder="Vorrat suchen" value={query} onChange={(e) => props.setQuery(e.target.value)} data-testid="pantry-search" />
        {edits && (
          <button className="primary" onClick={() => props.onNew(FOOD_PROFILE.id)} data-testid="pantry-new">
            + Neu
          </button>
        )}
      </div>
      {aisles.length > 1 && (
        <label>
          <span className="muted">Abteilung</span>
          <select value={aisle} onChange={(e) => props.setAisle(e.target.value)} data-testid="pantry-aisle">
            <option value="">Alle Abteilungen</option>
            {aisles.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="muted">
        {rows.length} {rows.length === 1 ? "Eintrag" : "Einträge"}
      </p>
      <ul className="list">
        {rows.map((a) => {
          const s = stock.get(a.id);
          return (
            <li key={a.id} className={props.selected === a.id ? "selected" : ""}>
              <button className={`row between ${s && s.qty < 0 ? "negative" : ""}`} onClick={() => props.onShow(a.id)}>
                <span className="named-emoji">
                  <Emoji fields={a.fields} />
                  <span className="text">
                    {a.name}
                    <br />
                    <span className="muted">{String(a.fields.aisle ?? "")}</span>
                  </span>
                </span>
                <Side a={a} qty={s?.qty ?? 0} />
              </button>
            </li>
          );
        })}
      </ul>
      {all.length === 0 && (
        <p>
          {edits ? (
            <>
              Noch nichts im Vorrat. Leg das Erste mit <span className="nowrap">„+ Neu“</span> an.
            </>
          ) : (
            "Noch nichts im Vorrat."
          )}
        </p>
      )}
      {props.tableDone && (
        <p className="notice" data-testid="pantry-table-done">
          {props.tableDone}
        </p>
      )}
      {edits && <PantryTableCard catalog={catalog} onPlan={props.onTable} />}
    </div>
  );
}

/** „28.10.2026“ from an ISO day. */
const shortDay = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/** Aisles in the order of the walk through the shop (as on the shopping list). */
const PANTRY_ORDER: string[] = PANTRY_PROFILES.flatMap((p) => p.fields.find((f) => f.key === "aisle")?.options ?? []);

function PantryDetail({ a, catalog, stock, onBack, setView }: { a: Article; catalog: Catalog; stock: Stock; onBack?: () => void; setView: (v: View) => void }) {
  const s = stock.get(a.id);
  const ledger = useLedger();
  const f = a.fields;
  const profile = profileById(String(f.profile));
  const base = baseProduct(f.base);
  const { store } = useStore();
  const rights = store.rights();
  const books = (store.config?.lagers ?? []).some((l) => canBook(rights, l));
  // Food with lots, some of it outside a freezer, and a freezer to book into.
  const freezerOf = (lager: string) => isFreezer({ id: lager, name: catalog.lagers.get(lager)?.name ?? "" });
  const canFreeze =
    a.tracking === "lot" &&
    freezable(a.fields) &&
    [...(s?.at.entries() ?? [])].some(([loc, q]) => q > 0 && !freezerOf(catalog.locations.get(loc)?.lager ?? "")) &&
    (store.config?.lagers ?? []).some((l) => canBook(rights, l) && freezerOf(l) && [...catalog.locations.values()].some((x) => x.lager === l));
  return (
    <div className="wines">
      {onBack && (
        <button className="link" onClick={onBack}>
          ← Alle Vorräte
        </button>
      )}
      <h2>
        <Emoji fields={a.fields} />
        <span data-testid="pantry-title">{a.name}</span>
      </h2>
      <p className="muted">{[base ? `${base.emoji ?? ""} ${base.names[0]}`.trim() : "", f.aisle].filter(Boolean).join(" · ")}</p>
      <div className="card">
        <div className="row between">
          <strong>Bestand</strong>
          <strong data-testid="pantry-stock">{s?.qty ? fmt(a, s.qty) : "leer"}</strong>
        </div>
        {s && s.at.size > 0 && (
          <ul className="detail" data-testid="pantry-places">
            {[...s.at.entries()]
              .filter(([, q]) => q !== 0)
              // Storages in the order they were set up (B5).
              .sort(([x], [y]) => compareLagers((id) => catalog.lagers.get(id)?.name ?? id)(catalog.locations.get(x)?.lager ?? x, catalog.locations.get(y)?.lager ?? y))
              .map(([loc, q]) => {
                const l = catalog.locations.get(loc);
                const lager = l?.lager ?? "";
                // The location only where its storage has more than one (B9, as Fristen, B6).
                const several = [...catalog.locations.values()].filter((x) => x.lager === lager).length > 1;
                // Best-before per lot under the place (C5), soonest first.
                const lots = (ledger ? stockLines(ledger) : [])
                  .filter((x) => x.article === a.id && x.location === loc && x.qty > 0 && ledger?.lots.get(x.lot)?.bestBefore)
                  .map((x) => ({ qty: x.qty, date: ledger!.lots.get(x.lot)!.bestBefore!.slice(0, 10) }))
                  .sort((x, y) => x.date.localeCompare(y.date));
                return (
                  <li key={loc}>
                    <div className="row between">
                      <span className="place">
                        {catalog.lagers.get(lager)?.name ?? lager}
                        {several && <span className="muted"> · {l?.name ?? loc}</span>}
                        {lots.length === 1 && (
                          <span className="muted mhd" data-testid="pantry-mhd">
                            MHD {shortDay(lots[0]!.date)}
                          </span>
                        )}
                      </span>
                      <Side a={a} qty={q} />
                    </div>
                    {lots.length > 1 &&
                      lots.map((x, i) => (
                        <div key={i} className="row between lot">
                          <span className="muted" data-testid="pantry-mhd">
                            MHD {shortDay(x.date)}
                          </span>
                          <Side a={a} qty={x.qty} />
                        </div>
                      ))}
                  </li>
                );
              })}
          </ul>
        )}
        {s && s.value > 0 && pricesShown(catalog, store.prices) && <p className="muted">Wert zum Einkaufspreis: {euro(s.value)}</p>}
        {books && (
          <div className="buttons">
            <a className="button primary" href={`#/buchen?einlagern=${encodeURIComponent(a.id)}`} data-testid="pantry-receive">
              Einlagern
            </a>
            <a className="button" href={`#/buchen?entnehmen=${encodeURIComponent(a.id)}`}>
              {profile.vocabulary.take.private}
            </a>
            {canFreeze && (
              // Into the freezer with a new best-before (C1).
              <a className="button" href={`#/buchen?einfrieren=${encodeURIComponent(a.id)}`} data-testid="pantry-freeze">
                Einfrieren
              </a>
            )}
          </div>
        )}
      </div>
      <Reserved a={a} catalog={catalog} stock={s?.qty ?? 0} />
      {s && s.qty > 0 && <Opened a={a} catalog={catalog} at={s.at} />}
      <table className="facts">
        <tbody>
          {/* The base product first: through it EinkaufsZettel and SpeisePlan find the article (Martin, 05.11.2026). */}
          {baseProduct(f.base) && (
            <tr data-testid="pantry-base">
              <th>Grundprodukt</th>
              <td>{baseProduct(f.base)!.names[0]}</td>
            </tr>
          )}
          {profile.fields.filter((field) => field.key !== "base" && shown(f, field) !== "").map((field) => (
            <tr key={field.key}>
              <th>{field.label}</th>
              <td>{shown(f, field)}</td>
            </tr>
          ))}
          {a.barcodes.length > 0 && (
            <tr>
              <th>Strichcode</th>
              <td>{a.barcodes.join(", ")}</td>
            </tr>
          )}
        </tbody>
      </table>
      {/* Right under the facts it changes, not after the history (Martin, 05.11.2026). */}
      {editsMasterData(rights.role) && (
        <div className="buttons">
          <button onClick={() => setView({ kind: "edit", id: a.id, profile: profile.id })} data-testid="pantry-edit">
            Bearbeiten
          </button>
        </div>
      )}
      <History a={a} catalog={catalog} />
    </div>
  );
}

/**
 * The opened container with a tap: „voll, halb, wenig, leer“ (docs/18-vorratsbuch.md, Einheiten).
 * It books a count of the location where the most lies: whole containers plus the estimate.
 */
function Opened({ a, catalog, at }: { a: Article; catalog: Catalog; at: Map<string, number> }) {
  const { store } = useStore();
  const ledger = useLedger();
  const [done, setDone] = useState<string | null>(null);
  // Eggs counted one by one have no opened box.
  const size = pantrySize(a.fields);
  const where = [...at.entries()].filter(([, q]) => q > 0).sort((x, y) => y[1] - x[1])[0]?.[0];
  if (!size || size <= 1 || !where || !ledger) return null;
  const location = catalog.locations.get(where);
  if (!location || !canBook(store.rights(), location.lager)) return null;
  async function set(level: Level) {
    const now = quantityAt(ledger!, a.id, where!);
    const whole = Math.floor(Math.max(0, now - 1) / size!);
    const qty = whole * size! + levelQty(level, size!);
    await store.book({ kind: "count", article: a.id, lager: location!.lager, location: where!, qty } as Booking);
    // What is there now, not what was booked (B14): „Angebrochene Packung leer – noch 1 Pck.“
    const total = [...at.values()].reduce((s, q) => s + q, 0) - now + qty;
    setDone(`${openedName(a.fields)} ${LEVELS.find((l) => l.id === level)!.label} – ${total > 0 ? `noch ${fmt(a, total)}` : "nichts mehr da"}`);
  }
  return (
    <div className="card">
      <strong>Angebrochen, wie viel ist noch drin?</strong>
      <p className="hint">Für die Packung, die offen ist; die vollen bleiben, wie sie sind. Eine Schätzung genügt.</p>
      <div className="levels">
        {LEVELS.map((l) => (
          <button key={l.id} className="wide" onClick={() => void set(l.id)} data-testid={`level-${l.id}`}>
            {l.label}
          </button>
        ))}
      </div>
      {done && (
        <p className="muted" data-testid="level-done">
          {done}
        </p>
      )}
    </div>
  );
}

/** The profile a base product belongs to: a household aisle is Haushalt, everything else Lebensmittel. */
const profileForBase = (p: BaseProduct | undefined): Profile => (p && HOUSEHOLD_AISLES.includes(p.aisle) ? HOUSEHOLD_PROFILE : FOOD_PROFILE);

/**
 * The form for a new article or a change: base product first, the rest as the profile says.
 * `preset`: the fields of a new one to start with (a scanned product, F30); `extra`: fields a new
 * one gets besides the form's (its barcodes).
 */
export function PantryForm({ article, profile, wide = false, seed = "", preset, extra, onDone }: { article?: Article; profile: Profile; wide?: boolean; seed?: string; preset?: Record<string, unknown>; extra?: Record<string, unknown>; onDone: (id: string | null) => void }) {
  const { store } = useStore();
  const ledger = useLedger();
  // A new one takes its profile from the base product chosen (Spülmittel → Haushalt): one „+ Neu“
  // for both, the split stays inside (Martin, 05.11.2026). A saved one keeps its profile.
  const [P, setP] = useState(profile);
  const f = article?.fields ?? preset ?? {};
  const allFields = [...new Map(PANTRY_PROFILES.flatMap((p) => p.fields).map((field) => [field.key, field])).values()];
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(allFields.map((field) => [field.key, editable(f, field.key, field.type)])));
  const [baseQuery, setBaseQuery] = useState(() => baseProduct(f.base)?.names[0] ?? seed);
  const [error, setError] = useState<string | null>(null);
  const booked = article ? (ledger ? [...(store.replica?.journal() ?? [])].some((b) => b.kind !== "cancel" && b.kind !== "convert" && b.article === article.id) : true) : false;
  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }));
  // Changes not saved yet: a tap on the tab asks first (B8).
  const [initial] = useState(() => JSON.stringify(values) + baseQuery);
  useEffect(() => {
    unsaved.dirty = JSON.stringify(values) + baseQuery !== initial;
  }, [values, baseQuery, initial]);
  useEffect(
    () => () => {
      unsaved.dirty = false;
    },
    [],
  );
  const chosenBase = baseProduct(values.base);
  const hits = !chosenBase || baseQuery !== chosenBase.names[0] ? searchBaseProducts(baseQuery, 6, { drinks: true }) : [];

  function pick(p: BaseProduct) {
    const next = article ? P : profileForBase(p);
    if (next !== P) setP(next);
    setBaseQuery(p.names[0]!);
    setValues((v) => ({
      ...v,
      base: p.id,
      // Another profile has other aisles: the base product's.
      aisle: (next === P && v.aisle) || aisleOf(p.aisle)?.name || "",
      unit: v.unit || suggestedUnit(p),
      countBy: v.countBy || ((v.unit || suggestedUnit(p)) === "Stk." ? (suggestedCountBy(p) ?? "") : ""),
      product: v.product || p.names[0]!,
      // How it is kept, from its aisle (C3); it also says where it goes when received (A5).
      keep: next.fields.some((field) => field.key === "keep") ? v.keep || (suggestedKeep(p) ?? "") : "",
    }));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const missing = P.fields.filter((field) => field.required && !values[field.key]?.trim()).map((field) => field.label);
    if (missing.length) return setError(`Bitte ausfüllen: ${missing.join(", ")}`);
    const fields: Record<string, unknown> = {};
    for (const field of P.fields) {
      const raw = (values[field.key] ?? "").trim();
      let value: unknown = raw === "" ? null : raw;
      if (raw !== "" && field.type === "number") {
        const n = Number(raw.replace(",", "."));
        if (!Number.isFinite(n) || n < 0) return setError(`${field.label}: bitte eine Zahl`);
        value = field.key === "size" ? Math.round(n) : n;
      } else if (raw !== "" && field.type === "yesno") value = raw === "1";
      else if (raw !== "" && field.type === "multi") value = field.key === "allergens" ? allergenIds(raw) : raw.split(",").map((x) => x.trim()).filter(Boolean);
      fields[field.key] = value;
    }
    if (fields.size === 0) fields.size = null;
    const id = article?.id ?? uuidv7(Date.now());
    const all = { ...(article ? {} : extra), ...fields, profile: P.id, name: pantryName({ ...fields, profile: P.id }), category: fields.aisle, tracking: P.tracking };
    const changed = Object.fromEntries(Object.entries(all).filter(([k, v]) => (article ? JSON.stringify(article.fields[k] ?? null) !== JSON.stringify(v ?? null) : v !== null)));
    if (Object.keys(changed).length > 0) await store.edit("article", id, changed);
    onDone(id);
  }

  const groups = Object.entries(P.groups);
  return (
    <form className={`wine-form ${wide ? "wide" : ""}`} noValidate onSubmit={save}>
      <button type="button" className="link" onClick={() => onDone(article?.id ?? null)}>
        ← Abbrechen
      </button>
      <h2>{article ? "Vorrat bearbeiten" : "Neuer Vorrat"}</h2>
      <div className="groups">
        {groups.map(([group, title]) => (
          <section key={group} className="card">
            <strong>{title}</strong>
            {group === "main" && (
              <label>
                Grundprodukt *
                <input value={baseQuery} onChange={(e) => setBaseQuery(e.target.value)} placeholder={P.fields.find((field) => field.key === "base")?.hint} autoComplete="off" data-testid="field-base" />
                {hits.length > 0 && (
                  <span className="suggestions">
                    {hits.map((p) => (
                      <button type="button" key={p.id} className="wide" onClick={() => pick(p)} data-testid={`base-${p.id}`}>
                        <span className="emoji">{p.emoji ?? aisleOf(p.aisle)?.emoji ?? ""}</span>
                        <span>
                          {p.names[0]}
                          <span className="muted">{aisleOf(p.aisle)?.name}</span>
                        </span>
                      </button>
                    ))}
                  </span>
                )}
                <span className="hint">Wie auf einem Rezept oder Einkaufszettel. Darüber finden die Apps EinkaufsZettel und SpeisePlan den Vorrat.</span>
              </label>
            )}
            {P.fields
              .filter((field) => field.group === group && field.key !== "base")
              .map((field) => (
                <FieldInput key={field.key} field={field.key === "size" && values.unit ? { ...field, unit: values.unit } : field} values={values} set={set} locked={field.key === "unit" && booked} />
              ))}
          </section>
        ))}
      </div>
      {error && <p className="error-box">{error}</p>}
      <button className="primary wide" type="submit" data-testid="pantry-save">
        Speichern
      </button>
    </form>
  );
}
